import React, { useEffect, useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { useSettings } from "../context/SettingsContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { formatApiError } from "../lib/api";
import { Globe } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const { t, lang, setLang } = useLang();
  const { logoUrl } = useSettings();
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    try {
      if (sessionStorage.getItem("session_expired") === "1") {
        sessionStorage.removeItem("session_expired");
        toast(t("common.sessionExpired"));
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (user) return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(identifier, password);
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-160px)] grid lg:grid-cols-2">
      {/* Left visual */}
      <div className="hidden lg:flex relative bg-[#0A1733] text-white p-12 items-end overflow-hidden">
        <div className="absolute inset-0 grain opacity-30" />
        <div
          className="absolute inset-0 opacity-40 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://static.prod-images.emergentagent.com/jobs/7ddb6a00-8991-49e0-827a-1580bf147ddf/images/29f47cfd41f1e204d3880428211e1e5b1143b5ac15c18dd4cd8640a1a3937f3a.png')",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#0A1733] via-[#0A1733]/85 to-transparent" />
        <div className="relative z-10 max-w-md">
          <div className="label-mini text-white/70 mb-3">Apotheken Retouren-System</div>
          <h2 className="text-4xl font-bold leading-[1.05] tracking-tight">
            Effiziente Retourenverwaltung.
            <br />
            <span className="text-[#7AA7FF]">Made for warehouses.</span>
          </h2>
          <p className="mt-4 text-white/70 text-sm max-w-sm">
            Mobile-first. Schnell. Audit-sicher. Ein klares Dashboard für jede einzelne Retoure.
          </p>
        </div>
      </div>

      {/* Right form */}
      <div className="flex flex-col items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-sm">
          <div className="flex items-center justify-end mb-4">
            <button
              data-testid="login-lang-toggle"
              onClick={() => setLang(lang === "de" ? "en" : "de")}
              className="text-xs font-bold tracking-widest text-muted-foreground hover:text-foreground px-2 py-1 border border-border"
              style={{ borderRadius: 4 }}
            >
              <Globe className="w-3.5 h-3.5 inline mr-1" />
              {lang.toUpperCase()}
            </button>
          </div>

          <div className="flex flex-col items-center mb-8" data-testid="login-brand">
            {logoUrl ? (
              <img src={logoUrl} alt="Logo" className="h-16 w-auto object-contain" />
            ) : (
              <div
                className="w-16 h-16 bg-[#0055FF] flex items-center justify-center text-white font-bold text-3xl"
                style={{ borderRadius: 6 }}
              >
                R
              </div>
            )}
            <div className="mt-3 text-center">
              <div className="font-bold text-lg tracking-tight">{!logoUrl && "Apotheken RMS"}</div>
              <div className="label-mini mt-1">Retouren-Management</div>
            </div>
          </div>

          <h1 className="text-3xl font-bold tracking-tight">{t("login.title")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t("login.subtitle")}</p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label className="label-mini mb-1.5 block">{t("login.identifier")}</Label>
              <Input
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                autoFocus
                required
                data-testid="login-identifier-input"
                style={{ borderRadius: 4 }}
              />
            </div>
            <div>
              <Label className="label-mini mb-1.5 block">{t("login.password")}</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
                style={{ borderRadius: 4 }}
              />
            </div>
            <Button
              type="submit"
              disabled={busy}
              className="w-full bg-[#0055FF] hover:bg-[#0044CC] text-white"
              style={{ borderRadius: 4 }}
              data-testid="login-submit-button"
            >
              {busy ? t("common.loading") : t("login.submit")}
            </Button>
          </form>

          <p className="text-sm text-muted-foreground mt-6 text-center">
            {t("login.noAccount")}{" "}
            <Link to="/register" className="text-[#0055FF] font-medium hover:underline" data-testid="login-register-link">
              {t("login.registerLink")}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
